import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";

const TIMER_ENABLED = import.meta.env.VITE_ENABLE_TIMER === "true";

interface ActiveTimer {
  id: string;
  taskId: string;
  startTime: Date;
}

interface TimerContextType {
  activeTimer: ActiveTimer | null;
  elapsedTime: number;
  isRunning: boolean;
  startTimer: (taskId: string) => Promise<void>;
  stopTimer: () => Promise<void>;
  loading: boolean;
  error: string | null;
}

const TimerContext = createContext<TimerContextType | undefined>(undefined);

const ACTIVE_TIMER_KEY = "active_timer";

function storeActiveTimer(timer: ActiveTimer | null) {
  if (timer) {
    localStorage.setItem(
      ACTIVE_TIMER_KEY,
      JSON.stringify({
        ...timer,
        startTime: timer.startTime.toISOString(),
      }),
    );
  } else {
    localStorage.removeItem(ACTIVE_TIMER_KEY);
  }
}

function loadActiveTimer(): ActiveTimer | null {
  try {
    const stored = localStorage.getItem(ACTIVE_TIMER_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return {
      ...parsed,
      startTime: new Date(parsed.startTime),
    };
  } catch {
    return null;
  }
}

function TimerProviderInner({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!user) {
      setActiveTimer(null);
      return;
    }

    const checkActiveTimer = async () => {
      try {
        const { data, error: fetchError } = await supabase
          .from("time_entries")
          .select("id, task_id, start_time")
          .eq("user_id", user.id)
          .is("end_time", null)
          .maybeSingle();

        if (fetchError) throw fetchError;

        if (data && data.start_time) {
          const timer: ActiveTimer = {
            id: data.id,
            taskId: data.task_id!,
            startTime: new Date(data.start_time),
          };
          setActiveTimer(timer);
          storeActiveTimer(timer);
        } else {
          localStorage.removeItem(ACTIVE_TIMER_KEY);
          setActiveTimer(null);
        }
      } catch (err) {
        console.error("Error checking active timer:", err);
        const stored = loadActiveTimer();
        if (stored) {
          setActiveTimer(stored);
        }
      }
    };

    checkActiveTimer();
  }, [user]);

  useEffect(() => {
    if (activeTimer) {
      const updateElapsed = () => {
        const now = new Date();
        const elapsed = Math.floor((now.getTime() - activeTimer.startTime.getTime()) / 1000);
        setElapsedTime(elapsed);
      };

      updateElapsed();
      intervalRef.current = setInterval(updateElapsed, 1000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    } else {
      setElapsedTime(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [activeTimer]);

  const startTimer = useCallback(
    async (taskId: string) => {
      if (!user) {
        setError("You must be logged in to start a timer");
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const { data: existingTimer } = await supabase
          .from("time_entries")
          .select("id")
          .eq("user_id", user.id)
          .is("end_time", null)
          .maybeSingle();

        if (existingTimer) {
          await supabase
            .from("time_entries")
            .update({
              end_time: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            })
            .eq("id", existingTimer.id);
        }

        const { data: newTimer, error: createError } = await supabase
          .from("time_entries")
          .insert({
            task_id: taskId,
            user_id: user.id,
            start_time: new Date().toISOString(),
            end_time: null,
            billable: true,
          })
          .select()
          .single();

        if (createError) throw createError;

        const timer: ActiveTimer = {
          id: newTimer.id,
          taskId: newTimer.task_id!,
          startTime: new Date(newTimer.start_time!),
        };

        setActiveTimer(timer);
        storeActiveTimer(timer);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to start timer";
        setError(message);
        console.error("Error starting timer:", err);
      } finally {
        setLoading(false);
      }
    },
    [user],
  );

  const stopTimer = useCallback(async () => {
    if (!activeTimer || !user) return;

    setLoading(true);
    setError(null);

    try {
      const endTime = new Date().toISOString();

      const { error: updateError } = await supabase
        .from("time_entries")
        .update({
          end_time: endTime,
          updated_at: endTime,
        })
        .eq("id", activeTimer.id);

      if (updateError) throw updateError;

      setActiveTimer(null);
      storeActiveTimer(null);
      setElapsedTime(0);

      const start = new Date(activeTimer.startTime);
      const end = new Date(endTime);
      void Math.floor((end.getTime() - start.getTime()) / 1000);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to stop timer";
      setError(message);
      console.error("Error stopping timer:", err);
    } finally {
      setLoading(false);
    }
  }, [activeTimer, user]);

  return (
    <TimerContext.Provider
      value={{
        activeTimer,
        elapsedTime,
        isRunning: activeTimer !== null,
        startTimer,
        stopTimer,
        loading,
        error,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
}

export function TimerProvider({ children }: { children: ReactNode }) {
  const noopValue: TimerContextType = {
    activeTimer: null,
    elapsedTime: 0,
    isRunning: false,
    startTimer: async () => {},
    stopTimer: async () => {},
    loading: false,
    error: null,
  };

  if (!TIMER_ENABLED) {
    return <TimerContext.Provider value={noopValue}>{children}</TimerContext.Provider>;
  }

  return <TimerProviderInner>{children}</TimerProviderInner>;
}

export function useTimer(): TimerContextType {
  const context = useContext(TimerContext);
  if (context === undefined) {
    throw new Error("useTimer must be used within a TimerProvider");
  }
  return context;
}

export function useTimerSafe(): TimerContextType | null {
  const context = useContext(TimerContext);
  return context ?? null;
}